import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CatalogueMigration,
  CutoverReadiness,
  CutoverReadinessCheck,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { CutOverDialog } from "./CutOverDialog";
import { isProductionEnvironment } from "./environment";

const CHECK_BADGES: Record<
  CutoverReadinessCheck["status"],
  { label: string; variant: "success" | "error" | "warning" | "neutral" }
> = {
  ready: { label: "Ready", variant: "success" },
  not_ready: { label: "Not ready", variant: "error" },
  to_confirm: { label: "To confirm", variant: "warning" },
  evidence: { label: "Evidence", variant: "neutral" },
};

/**
 * "Ready to cut over?": the catalogue items applied, then every precondition
 * of the customer's cutover from the cutover module, each with its status and
 * evidence. Judgement is not encoded: staging's clean run and the stock facts
 * are shown for the person deciding. "Cut over…" is production's, for admins.
 */
export function ReadinessTab({
  migration,
  environment,
  onCutOver,
  onArtworkSearchStarted,
}: {
  migration: CatalogueMigration;
  environment: string;
  /** The cutover went through: the page re-reads the migration. */
  onCutOver: (message: string) => void;
  onArtworkSearchStarted: (message: string) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [readiness, setReadiness] = useState<CutoverReadiness | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [isCuttingOver, setIsCuttingOver] = useState(false);
  const [isFindingArtwork, setIsFindingArtwork] = useState(false);
  const [artworkError, setArtworkError] = useState(false);
  const isProduction = isProductionEnvironment(environment);
  const isCutOver = migration.cutover_state === "cut_over";

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    dataAdapter
      .getCutoverReadiness(migration.customer_id)
      .then((result) => {
        if (cancelled) return;
        setReadiness(result);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock_cutover.readiness.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, migration.customer_id, migration.cutover_state]);

  async function findArtwork() {
    setIsFindingArtwork(true);
    setArtworkError(false);
    try {
      await dataAdapter.findMigrationArtwork(migration.customer_id);
      onArtworkSearchStarted(
        "Looking for the standing artwork in Tharstern and the artwork server. It takes a few minutes; reload this page to see what it found.",
      );
    } catch (error) {
      console.error("stock_cutover.find_artwork.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setArtworkError(true);
    } finally {
      setIsFindingArtwork(false);
    }
  }

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-muted-foreground">Loading readiness…</div>
      </div>
    );
  }
  if (status === "error" || readiness === null) {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
      >
        <AlertTriangle className="size-4 shrink-0" />
        Couldn't load the readiness checklist. Please try again.
      </div>
    );
  }

  return (
    <Card className="p-5 space-y-4">
      <h2 className="text-lg">
        {isCutOver ? "What was ready at the cutover" : "Ready to cut over?"}
      </h2>
      <ul className="divide-y divide-border">
        <li className="flex items-start justify-between gap-4 py-2 text-sm">
          <span>Catalogue items applied</span>
          <Badge
            variant={
              migration.items_applied >= migration.item_count
                ? "success"
                : "warning"
            }
          >
            {formatCount(migration.items_applied)} of{" "}
            {formatCount(migration.item_count)}
          </Badge>
        </li>
        {readiness.checks.map((check) => (
          <CheckRow key={check.key} check={check} />
        ))}
      </ul>
      {artworkError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          Couldn't start the artwork search. Please try again.
        </p>
      )}
      {!isCutOver && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => void findArtwork()}
              disabled={isFindingArtwork || !migration.has_migration}
            >
              {isFindingArtwork ? "Starting…" : "Find artwork"}
            </Button>
            <Button
              onClick={() => setIsCuttingOver(true)}
              disabled={!isProduction}
            >
              Cut over…
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Admins only. Production only: in staging the shadow runs instead.
          </p>
        </div>
      )}
      {isCuttingOver && (
        <CutOverDialog
          migration={migration}
          onClose={() => setIsCuttingOver(false)}
          onCutOver={(message) => {
            setIsCuttingOver(false);
            onCutOver(message);
          }}
        />
      )}
    </Card>
  );
}

function CheckRow({ check }: { check: CutoverReadinessCheck }) {
  const badge = CHECK_BADGES[check.status];
  return (
    <li className="py-2 text-sm">
      <details>
        <summary className="flex cursor-pointer items-start justify-between gap-4">
          <span>{check.title}</span>
          <span className="flex flex-wrap items-center justify-end gap-2 text-right">
            <span className="text-muted-foreground">{check.summary}</span>
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </span>
        </summary>
        <div className="mt-2 space-y-2 pl-1 text-xs text-muted-foreground">
          {check.items.length > 0 && (
            <ul className="space-y-1 text-red-700 dark:text-red-300">
              {check.items.map((item) => (
                <li key={`${item.field}-${item.code}`}>
                  <span className="font-medium">{item.field}</span>:{" "}
                  {item.message}
                </li>
              ))}
            </ul>
          )}
          {check.evidence.length > 0 && (
            <ul className="space-y-0.5">
              {check.evidence.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </li>
  );
}
